import React from "react";
import { useStaticQuery, graphql } from "gatsby";
import styled, { css } from "styled-components";
import Section from "./Section";
import Container from "./Container";
import Grid from "./Grid";
import { ContactEnquiryForm } from "./ContactEnquiryForm";
import GoogleMapReact from "google-map-react";
import MapMarkerLargeIcon from "./Icons/MapMarkerLarge";
import CallTrackLink from "./CalltrackLink";
import ContactTelephoneWrapper from "./ContactTelephoneWrapper";
import { HtmlContent } from "./Content";

const StyledLocationPin = styled.a`
  ${({ ...props }) => css`
    svg {
      color: ${props.theme.colors.primary};
    }
  `}
`;

const ContactHeadingStyles = styled.h3`
  ${({ ...props }) => css`
    h3 {
      margin-bottom: 16px;
    }
  `}
`;

const MapStyles = styled.div`
  ${({ ...props }) => css`
    margin-top: 24px;
    overflow: hidden;
    border-radius: 4px;
    height: 500px;
    width: 100%;

    /* Desktop styles */
    @media (min-width: ${props.theme.breakpoints.md}) {
      margin-top: 48px;
    }
  `}
`;

const ContactBannerStyles = styled.div`
  ${({ ...props }) => css`
    background-color: ${props.theme.colors.lightPrimary};
    border-radius: 4px;
    overflow: hidden;
    padding: 32px;
    margin-bottom: 24px;

    a {
      color: ${props.theme.colors.primary};
    }

    strong a {
      font-size: 2rem;
      line-height: 44px;
    }

    p {
      font-size: 1.125rem;
      line-height: 24px;
    }

    @media (min-width: ${props.theme.breakpoints.md}) {
      margin-bottom: 48px;
    }
  `}
`;

const LocationPin = ({ url }) => (
  <StyledLocationPin
    href={url}
    target="_blank"
    rel="noreferrer"
    style={{
      transform: "translate(-50%, -50%)",
      display: "block",
    }}
  >
    <MapMarkerLargeIcon />
  </StyledLocationPin>
);

export const ContactMapAndForm = () => {
  const data = useStaticQuery(graphql`
    query ContactQuery {
      wp {
        siteSettings {
          siteSettings {
            offices {
              lat
              long
              officeAddress
              officeName
              googleMapLink
            }
            openingTimes
            companyEmailPreText
            companyEmail
            companyName
          }
        }
      }
    }
  `);

  const { offices, companyEmail, companyName, openingTimes, companyEmailPreText } =
    data.wp.siteSettings.siteSettings;

  return (
    <Section>
      <Container>
        <Grid columnGap="loose">
          <Grid item md="8" columnGap="loose">
            <ContactBannerStyles>
              <h2>Contact {companyName}</h2>
              <ContactTelephoneWrapper>
                <strong>
                  <CallTrackLink />
                </strong>
              </ContactTelephoneWrapper>
              <p>{openingTimes}</p>
              <p>
                {companyEmailPreText}{" "}
                <a href={`mailto:${companyEmail}`}>{companyEmail}</a>
              </p>
            </ContactBannerStyles>
            <Grid columnGap="loose">
              {offices
                ? offices.map((office) => (
                    <Grid item md="6" columnGap="loose" key={office.officeName}>
                      <ContactHeadingStyles>
                        {office.officeName}
                      </ContactHeadingStyles>
                      <address><HtmlContent htmlString={office.officeAddress} /></address>
                    </Grid>
                  ))
                : `No content`}
            </Grid>
            {typeof window !== "undefined" && (
              <MapStyles>
                <GoogleMapReact
                  bootstrapURLKeys={{
                    key: "AIzaSyCvZpNIZrFt1qFQGTEMfaVtBNWZeRZyI30",
                  }}
                  defaultCenter={{
                    lat: 53.311248113893356,
                    lng: -1.4759457762030106,
                  }}
                  defaultZoom={6}
                >
                  {offices.length > 0
                    ? offices.map((office) => {
                        return (
                          <LocationPin
                            lat={Number(office.lat)}
                            lng={Number(office.long)}
                            url={office.googleMapLink}
                            key={office.googleMapLink}
                          />
                        );
                      })
                    : ``}
                </GoogleMapReact>
              </MapStyles>
            )}
          </Grid>

          <Grid item md="4" columnGap="loose">
            <ContactEnquiryForm />
          </Grid>
        </Grid>
      </Container>
    </Section>
  );
};
